import React from 'react';
import { TestMetadata } from '../types/testy';
import { useEntitlements } from '../hooks/useEntitlements';

interface TestCardProps {
  test: TestMetadata;
  onStartTest: (testId: string) => void;
  onNavigate?: (section: string) => void;
  className?: string;
}

const TestCard: React.FC<TestCardProps> = ({ test, onStartTest, onNavigate, className = '' }) => {
  const { hasPro, loading } = useEntitlements();
  
  const formatDuration = (durationSec: number): string => {
    const minutes = Math.floor(durationSec / 60);
    return `${minutes} minut`;
  };

  const handleStartClick = () => {
    onStartTest(test.id);
  };

  const handleUpgradeClick = () => {
    // Navigate to upgrade page using the navigation prop
    if (onNavigate) {
      onNavigate('upgrade');
    } else {
      // Fallback to direct navigation if onNavigate is not provided
      window.location.href = '/upgrade';
    }
  };

  return (
    <article 
      className={`bg-white dark:bg-dark-card rounded-lg p-4 sm:p-6 border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-sm hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:hover:shadow-md transition-shadow duration-200 ${className}`}
      aria-labelledby={`test-title-${test.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <div className="flex items-center gap-2">
              <h3 
                id={`test-title-${test.id}`}
                className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary font-poppins"
              >
                {test.title}
              </h3>
              {!test.isFreeSample && (
                <div title="Pouze pro Pro uživatele">
                  <svg 
                    className="w-4 h-4 text-amber-500 dark:text-amber-400 flex-shrink-0" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    aria-label="Pro test"
                  >
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                    <path d="M12 7c-1.1 0-2 .9-2 2v2c0 .55.45 1 1 1h2c.55 0 1-.45 1-1V9c0-1.1-.9-2-2-2z" fill="white"/>
                  </svg>
                </div>
              )}
            </div>
            {test.isFreeSample && (
              <span 
                className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full self-start"
                role="status"
                aria-label="Bezplatný test"
              >
                Zdarma
              </span>
            )}
            {!test.isFreeSample && (
              <span 
                className="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 rounded-full self-start"
                role="status"
                aria-label="Pro test"
              >
                Pro
              </span>
            )}
          </div>
          
          <div 
            className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-light-text-secondary dark:text-dark-text-secondary font-poppins"
            role="list"
            aria-label="Informace o testu"
          >
            <div className="flex items-center gap-1" role="listitem">
              <svg 
                className="w-4 h-4 flex-shrink-0" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                <span className="sr-only">Časový limit: </span>
                {formatDuration(test.durationSec)}
              </span>
            </div>
            
            <div className="flex items-center gap-1" role="listitem">
              <svg 
                className="w-4 h-4 flex-shrink-0" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>
                <span className="sr-only">Počet otázek: </span>
                {test.questionCount} otázek
              </span>
            </div>
            
            <div className="flex items-center gap-1" role="listitem">
              <svg 
                className="w-4 h-4 flex-shrink-0" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>
                <span className="sr-only">Počet sekcí: </span>
                {test.sectionCount} sekce
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex-shrink-0">
          {test.isFreeSample || hasPro ? (
            <button
              onClick={handleStartClick}
              className="w-full sm:w-auto px-6 py-2 bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-dark-card"
              aria-describedby={`test-title-${test.id}`}
            >
              Začít test
            </button>
          ) : (
            <button
              onClick={handleUpgradeClick}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-2 bg-amber-600 hover:bg-amber-700 focus:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 dark:focus:bg-amber-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-dark-card disabled:opacity-50 disabled:cursor-not-allowed"
              aria-describedby={`test-title-${test.id}`}
            >
              {loading ? 'Načítání...' : 'Jen pro uživatele PRO'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default TestCard;